import React, { useEffect, useRef, useState } from "react";

/**
 * A small popover menu anchored to its trigger — the account menu, row overflow actions.
 *
 * Closes on outside click and on Escape, and the trigger keeps its own styling: this only owns
 * the open/closed state and the panel, so a button, an avatar or a table cell can all use it.
 */
export const Menu: React.FC<{
    trigger: (props: { open: boolean; toggle: () => void }) => React.ReactNode;
    children: (props: { close: () => void }) => React.ReactNode;
    align?: "left" | "right";
    className?: string;
}> = ({ trigger, children, align = "right", className = "" }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    return (
        <div ref={wrapRef} className={`relative ${className}`}>
            {trigger({ open, toggle: () => setOpen((o) => !o) })}
            {open && (
                <div
                    className={`absolute top-[calc(100%+8px)] z-30 min-w-[210px] rounded-[13px] border border-[var(--line-2)] bg-[var(--surface)] p-1.5 shadow-[0_24px_60px_rgba(0,0,0,.5)] ${
                        align === "right" ? "right-0" : "left-0"
                    }`}
                    role="menu"
                >
                    {children({ close: () => setOpen(false) })}
                </div>
            )}
        </div>
    );
};

/** One row inside a Menu. */
export const MenuItem: React.FC<{
    onClick?: () => void;
    icon?: React.ReactNode;
    danger?: boolean;
    children: React.ReactNode;
}> = ({ onClick, icon, danger, children }) => (
    <button
        type="button"
        role="menuitem"
        onClick={onClick}
        className={`flex w-full items-center gap-2.5 rounded-[9px] px-3 py-2.5 text-left text-[13.5px] font-medium hover:bg-[var(--surface-2)] ${
            danger ? "text-[var(--crit)]" : "text-[var(--text)]"
        }`}
    >
        {icon}
        {children}
    </button>
);

/** Non-interactive heading block at the top of a Menu. */
export const MenuHeader: React.FC<{ title: string; sub?: string }> = ({ title, sub }) => (
    <div className="border-b border-[var(--line)] px-3 pb-2.5 pt-2">
        <div className="truncate text-[13.5px] font-semibold">{title}</div>
        {sub && <div className="mt-0.5 truncate text-[11.5px] text-[var(--muted)]">{sub}</div>}
    </div>
);
